"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense } from "react";
import dynamic from "next/dynamic";
import { AppProviders } from "@/components/AppProviders";
import { useAuth } from "@/components/AuthProvider";
import { OrgAccessSplash } from "@/components/OrgAccessSplash";
import { useUrlOrgAccessGate } from "@/hooks/useUrlOrgAccessGate";
import { useRepoPermissionsSync } from "@/hooks/useRepoPermissionsSync";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { DashboardShellFallback } from "@/app/DashboardShellFallback";
import { DashboardRouterProvider, useDashboardRouter } from "@/hooks/useDashboardRouter";
import { OrgUrlSyncProvider } from "@/components/OrgUrlSyncProvider";
import { SidebarLayout } from "@/components/SidebarLayout";
import { DesktopLayoutSkeleton } from "@/components/desktop/DesktopLayoutSkeleton";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { githubLogin } from "@/lib/github-login";

// Lazy, as in `_app.tsx`, so web visitors never download the desktop chrome.
const DesktopSidebarLayout = dynamic(() => import("@/components/desktop/DesktopSidebarLayout").then(mod => mod.DesktopSidebarLayout), {
  loading: () => <DesktopLayoutSkeleton />
});
const DesktopDragRegion = dynamic(() => import("@/components/desktop/DesktopLayoutSkeleton").then(mod => mod.DesktopDragRegion));

// The Chrome extension iframe (`?embed=extension`) renders the page bare, as
// `_app.tsx` does: no rail, sidebars, command palette, shortcuts modal, or
// the shell's prefetch hooks. The page still needs a Suspense boundary below
// AppProviders: pages that suspend at their root (the PR resolver reads
// useActiveGithubOrgForUser there) would otherwise bubble to the layout
// boundary, discarding the query client and auth tree and re-suspending on
// the fresh cache.
function DashboardShell(t0) {
  const $ = _c(20);
  const {
    children
  } = t0;
  const router = useDashboardRouter();
  const {
    user,
    logout
  } = useAuth();
  const orgAccess = useUrlOrgAccessGate();
  const {
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  let t1;
  if ($[0] !== activeNamedOrg || $[1] !== router.pathname || $[2] !== router.query.org) {
    t1 = typeof router.query.org === "string" && !router.pathname.startsWith("/admin/internal-monitors") ? activeNamedOrg : null;
    $[0] = activeNamedOrg;
    $[1] = router.pathname;
    $[2] = router.query.org;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const urlOrg = t1;
  useRepoPermissionsSync(orgAccess.state === "allowed" ? urlOrg : null);
  const surface = useCodesmithSurface(router.query.embed);
  if (orgAccess.state === "pending") {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <DashboardShellFallback />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (orgAccess.state === "denied") {
    let t2;
    if ($[5] !== surface) {
      t2 = surface === "desktop" && <DesktopDragRegion />;
      $[5] = surface;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    const t3 = orgAccess.org;
    const t4 = orgAccess.fallbackOrg;
    let t5;
    if ($[7] !== user) {
      t5 = githubLogin(user);
      $[7] = user;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== logout || $[10] !== orgAccess.fallbackOrg || $[11] !== orgAccess.org || $[12] !== t5) {
      t6 = <OrgAccessSplash org={t3} fallbackOrg={t4} username={t5} onLogout={logout} />;
      $[9] = logout;
      $[10] = orgAccess.fallbackOrg;
      $[11] = orgAccess.org;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t2 || $[15] !== t6) {
      t7 = <>{t2}{t6}</>;
      $[14] = t2;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    return t7;
  }
  const content = children;
  let t2;
  if ($[17] !== content || $[18] !== surface) {
    t2 = <OrgUrlSyncProvider>{surface === "extension" ? <Suspense fallback={<DashboardShellFallback />}>{content}</Suspense> : surface === "desktop" ? <DesktopSidebarLayout>{content}</DesktopSidebarLayout> : <SidebarLayout>{content}</SidebarLayout>}</OrgUrlSyncProvider>;
    $[17] = content;
    $[18] = surface;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  return t2;
}
export default function DashboardLayout(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DashboardShellFallback />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Suspense fallback={t1}><DashboardRouterProvider><AppProviders><DashboardShell>{children}</DashboardShell></AppProviders></DashboardRouterProvider></Suspense>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}